import React, { useMemo } from 'react'
import { IconCube, Text, csx } from '@vtex/admin-ui'
import { useIntl } from 'react-intl'
import type { TextProps } from 'recharts'

import { DetailsBlockTemplate } from './DetailsBlockTemplate'
import {
  useFormatCurrency,
  useOrderFormShippingEstimate,
} from '../../../../hooks'
import type { Package } from '../../../../../../core/typings'
import { getDetailsScheduledDelivery, hasScheduledDelivery } from '../../utils'
import { decorateDeliverySlaWithBestIndicators } from '../../utils/sla'

const getShippingPackageValue = (pkg: Package) => pkg.price / 100

type SlaIndicatorTextProps = TextProps

const SlaIndicatorText = ({ children }: SlaIndicatorTextProps) => (
  <Text variant="detail" tone="positive">
    {` (${children})`}
  </Text>
)

export type DeliveryDetailsBlockProps = {
  currentPackage: Package
}

export const getDeliveryMethodNameFromPkg = (pkg: Package) =>
  pkg.selectedSlaObj?.name ?? ''

export const DeliveryDetailsBlock = ({
  currentPackage,
}: DeliveryDetailsBlockProps) => {
  const { formatMessage } = useIntl()
  const { getEstimateMessage } = useOrderFormShippingEstimate()
  const formatCurrency = useFormatCurrency()
  const freeShipping = getShippingPackageValue(currentPackage) === 0
  const deliveryMethodName = getDeliveryMethodNameFromPkg(currentPackage)

  const shippingValue = freeShipping
    ? formatMessage({
        id: 'checkout.shipping.select-shipping-method.free-shipping',
      })
    : formatCurrency(getShippingPackageValue(currentPackage))

  const estimateMessage = getEstimateMessage(
    currentPackage.selectedSlaObj?.shippingEstimate ?? ''
  )

  const slaIndicators = useMemo(() => {
    if (!currentPackage.selectedSlaObj) {
      return {
        isCheapest: false,
        isFatest: false,
      }
    }

    return decorateDeliverySlaWithBestIndicators(
      currentPackage,
      currentPackage.selectedSlaObj
    )
  }, [currentPackage])

  return (
    <DetailsBlockTemplate
      data-testid="shipping-details-delivery-block"
      icon={<IconCube />}
      title={
        hasScheduledDelivery(currentPackage)
          ? formatMessage({
              id: 'checkout.shipping-method-selection.schedule-delivery-title',
            })
          : formatMessage({
              id: 'checkout.shipping.select-shipping-method.to-delivery',
            })
      }
      details={
        <Text
          variant="detail"
          tone="secondary"
          className={csx({ display: 'block' })}
        >
          {hasScheduledDelivery(currentPackage) ? (
            getDetailsScheduledDelivery(
              currentPackage,
              shippingValue,
              deliveryMethodName
            )
          ) : (
            <>
              {deliveryMethodName}
              {slaIndicators.isFatest && (
                <SlaIndicatorText>
                  {formatMessage({ id: 'checkout.shipping.sla-fastest' })}
                </SlaIndicatorText>
              )}
              {slaIndicators.isCheapest && (
                <SlaIndicatorText>
                  {formatMessage({ id: 'checkout.shipping.sla-cheapest' })}
                </SlaIndicatorText>
              )}
              {` - ${estimateMessage} - `}
              <Text
                variant="detail"
                tone={freeShipping ? 'positive' : 'secondary'}
              >
                {shippingValue}
              </Text>
            </>
          )}
        </Text>
      }
      currentPackage={currentPackage}
    />
  )
}
